import { useCallback, useEffect, useRef } from "react"
import { type Socket } from "socket.io-client"
import { useAccountId } from "resources/account"
import { getIntradaySocket, closeIntradaySocket } from "socket/intraday-connection"

export { computeListDiff } from "./list-diff"

const activeConsumersByAccountId = new Map<string, number>()

function registerIntradaySocketConsumer(accountId: string): void {
  activeConsumersByAccountId.set(accountId, (activeConsumersByAccountId.get(accountId) || 0) + 1)
}

function unregisterIntradaySocketConsumer(accountId: string): boolean {
  const currentCount = activeConsumersByAccountId.get(accountId) || 0
  if (currentCount <= 1) {
    activeConsumersByAccountId.delete(accountId)
    return true
  }
  activeConsumersByAccountId.set(accountId, currentCount - 1)
  return false
}

/** Exposed only for test isolation — resets the shared consumer registry between tests. */
export function _resetActiveConsumersForTesting(): void {
  activeConsumersByAccountId.clear()
}

export interface UseIntradaySocketConnectionResult {
  accountId: string
  registerSocketConsumer: () => void
}

/**
 * Terminology used by intraday socket hooks:
 * - register/unregister: client hook instances tracking local socket consumers
 * - subscribe/unsubscribe: server-side stream subscriptions for model provider IDs
 *
 * Manages the intraday socket lifecycle shared by all intraday stream hooks:
 * - Registers each hook instance as a socket consumer only once when activated
 * - Runs caller-supplied teardown on unmount/account change
 * - Closes the shared socket only when the last consumer unmounts
 *
 * Callers must memoize `onUnmount` with `useCallback(fn, [])` so the cleanup
 * effect is not re-triggered unnecessarily.
 */
export function useIntradaySocketConnection(
  onUnmount: (socket: Socket | undefined, accountId: string) => void,
): UseIntradaySocketConnectionResult {
  const accountId = useAccountId()
  const hasActivatedRef = useRef(false)

  const registerSocketConsumer = useCallback(() => {
    if (hasActivatedRef.current) return
    hasActivatedRef.current = true
    registerIntradaySocketConsumer(accountId)
  }, [accountId])

  useEffect(() => {
    const cleanupAccountId = accountId
    return () => {
      if (!hasActivatedRef.current) return
      onUnmount(getIntradaySocket(cleanupAccountId), cleanupAccountId)
      // Allow the same hook instance to activate again after account changes.
      hasActivatedRef.current = false
      const shouldCloseSocket = unregisterIntradaySocketConsumer(cleanupAccountId)
      if (shouldCloseSocket) {
        closeIntradaySocket(cleanupAccountId)
      }
    }
  }, [accountId, onUnmount])

  return { accountId, registerSocketConsumer }
}
